import moment from "moment";

const toDateKey = value =>
  value && moment.parseZone(value).isValid()
    ? moment.parseZone(value).format("YYYY-MM-DD")
    : null;

const associatedInstanceIds = records =>
  Array.from(
    new Set(
      (records || [])
        .map(record => record?.instanceId)
        .filter(instanceId => instanceId != null),
    ),
  );

const exhibitionEventTypeByName = {
  Artfair: "art-fair",
  Group: "group",
  Solo: "solo",
  "Viewing Room": "viewing-room",
};

const normaliseEventRange = event => {
  const date = toDateKey(event.date);
  const endDate = toDateKey(event.endDate) || date;
  return {
    ...event,
    date,
    endDate: endDate < date ? date : endDate,
  };
};

const buildLogisticsEvents = ({
  exhibitions = [],
  jobs = [],
  reservationInstances = [],
  transit,
}) =>
  [
    ...jobs
      .filter(job => job?.dueDate)
      .map(job => ({
        date: job.dueDate,
        id: `job-${job.id}`,
        instanceIds: associatedInstanceIds(job.jobItems),
        isCompleted: job.statusId === 3,
        label: job.jobType?.description || job.description || `Job ${job.id}`,
        type: "job",
      })),
    ...exhibitions
      .filter(
        exhibition =>
          exhibition?.startDate &&
          exhibitionEventTypeByName[exhibition.type],
      )
      .map(exhibition => ({
        date: exhibition.startDate,
        endDate: exhibition.endDate,
        href: `/exhibition/${exhibition.id}/details`,
        id: `exhibition-${exhibition.id}`,
        instanceIds: associatedInstanceIds(
          exhibition.exhibitionWorks?.filter(work => work?.removed !== -1),
        ),
        label: exhibition.title,
        type: exhibitionEventTypeByName[exhibition.type],
      })),
    ...reservationInstances
      .filter(
        reservationInstance =>
          reservationInstance?.reservation?.categoryId === 1 &&
          reservationInstance.reservation.date,
      )
      .map(reservationInstance => {
        const reservation = reservationInstance.reservation;
        return {
          date: reservation.date,
          endDate: reservation.dateDue,
          href: `/reservations/${reservation.id}/details`,
          id: `reservation-${reservation.id}-${reservationInstance.id}`,
          instanceIds: reservationInstance.instanceId
            ? [reservationInstance.instanceId]
            : [],
          isCompleted: reservationInstance.released === -1,
          label: [
            reservation.type?.description ||
              reservation.description ||
              `Reservation ${reservation.id}`,
            reservation.reservationUser?.name
              ? `Reserved for ${reservation.reservationUser.name}`
              : null,
          ]
            .filter(Boolean)
            .join(" · "),
          type: "reservation",
        };
      }),
    ...(transit?.shipment?.id &&
    transit?.shipment?.dispatched &&
    !transit?.shipment?.delivered &&
    transit?.siteId == null
      ? [
          {
            date:
              transit.shipment.dispatched ||
              transit.shipment.date ||
              moment().format("YYYY-MM-DD"),
            endDate:
              transit.shipment.delivered || moment().format("YYYY-MM-DD"),
            href: `/shipments/${transit.shipment.id}/details`,
            id: `transit-${transit.shipment.id}`,
            instanceIds: transit.instanceId ? [transit.instanceId] : [],
            label: transit.label || "In transit",
            type: "transit",
          },
        ]
      : []),
  ]
    .map(normaliseEventRange)
    .filter(event => event.date);

const eventsOnDate = (events, dateKey) =>
  (events || []).filter(
    event => event.date <= dateKey && event.endDate >= dateKey,
  );

const getMonthDays = month => {
  const firstDay = moment(month).startOf("month").startOf("week");
  return Array.from({ length: 42 }, (_, index) => {
    const date = firstDay.clone().add(index, "days");
    return {
      date,
      dateKey: date.format("YYYY-MM-DD"),
      isCurrentMonth: date.month() === moment(month).month(),
      isToday: date.isSame(moment(), "day"),
    };
  });
};

const formatShipmentEndpoint = (shipment, prefix) =>
  [
    shipment?.[`${prefix}Company`],
    shipment?.[`${prefix}Name`],
    shipment?.[`${prefix}Address`],
    shipment?.[`${prefix}Town`],
  ]
    .filter(Boolean)
    .join(", ") || "Unknown";

export {
  buildLogisticsEvents,
  eventsOnDate,
  formatShipmentEndpoint,
  getMonthDays,
  toDateKey,
};
